
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { AdminUser } from '@/store/modules/Members/AdminsModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'admins-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('adminUsersUpdating'), [
          translate('members'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_ADMIN_USER,
        route.params.id
      );

      const admin = ref<AdminUser>(data.data);
      const ppmNotificationLevel = ref<number | null>(
        (data.data as any)?.ppmNotificationLevel ?? null
      );

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const adminSchema = Yup.object().shape({
        name: Yup.string()
          .required(() => translate('FULL_NAME_IS_REQUIRED_FIELD'))
          .label('Name'),
        email: Yup.string()
          .email(() => translate('EMAIL_NOT_VALID'))
          .required(() => translate('EMAIL_IS_REQUIRED_FIELD'))
          .label('Email'),
        password: Yup.string()
          .min(4, () => translate('PASSWORD_MIN_4'))
          .max(20, () => translate('PASSWORD_MAX_20'))
          .matches(/^(?=.*[a-z])/, translate('PASSWORD_AT_LEAST_ONE_LOWER'))
          .matches(/^(?=.*[A-Z])/, translate('PASSWORD_AT_LEAST_ONE_UPPER'))
          .matches(/^(?=.*\d)/, translate('PASSWORD_AT_LEAST_ONE_NUMBER'))
          .matches(
            /^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/,
            translate('PASSWORD_AT_LEAST_ONE_SPECIAL')
          )
          .label('Password'),
        phone: Yup.string()
          .matches(/^\+?\d{8,15}$/, {
            excludeEmptyString: true,
            message: () => translate('PHONE_NOT_VALID'),
          })
          .label('Phone'),
        verifyPassword: Yup.string()
          .when('password', {
            is: (password) => password?.length > 0,
            then: Yup.string().required(() =>
              translate('VERIFY_PASSWORD_IS_REQUIRED_FIELD')
            ),
          })
          .oneOf([Yup.ref('password'), null], () =>
            translate('PASSWORDS_MUST_MATCH')
          )
          .label('Verify Password'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        delete values.verifyPassword;

        await store.dispatch(Actions.UPDATE_ADMIN_ACCOUNT, {
          id: admin.value?.id,
          data: values,
        });
        if (!Object.keys(store.getters.getErrors).length) {
          await store
            .dispatch(Actions.SET_PPM_RECIPIENT_LEVEL, {
              userId: admin.value?.id,
              data: { level: ppmNotificationLevel.value },
            })
            .catch(() => undefined);
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_ADMIN'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'admins-listing' });
          });
        }
      };

      await store.dispatch(Actions.GET_ROLES, { limit: 50 });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        roles: computed(() => store.getters.rolesList),
        onSubmitUpdate,
        submitButton,
        translate,
        adminSchema,
        ppmNotificationLevel,
        goBack,
        admin,
        can,
      };
    },
  });
